import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Container, Typography } from '@material-ui/core';
import { Add as PlusIcon } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import LiveRegionStatus from 'components/LiveRegionStatus';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { ProcessingActivity } from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ropaReviewsApi from 'config/api/ropaReviews/ropaReviews';
import canAccessProcessingActivitiesAdmin from 'config/permissions/canAccessProcessingActivitiesAdmin';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import ProcessingActivitiesMetricsSection from './_components/ProcessingActivitiesMetricsSection';
import ProcessingActivitiesTable from './_components/ProcessingActivitiesTable';
import ProcessingActivitiesToolbar from './_components/ProcessingActivitiesToolbar';
import getProcessingActivityArchiveErrorMessage from './getProcessingActivityArchiveErrorMessage';
import {
  buildProcessingActivitiesTableAvailableFilters,
  DEFAULT_PROCESSING_ACTIVITIES_FILTERS,
  hasProcessingActivitiesListFiltersApplied,
  mergeTableFiltersIntoFilterValues,
  processingActivitiesFilterValuesToTableFilters,
  type ProcessingActivitiesFilterValues,
  type ProcessingActivitiesSort,
  type ProcessingActivitiesTableFilterState,
} from './processingActivitiesFilters';
import useStyles from './ProcessingActivitiesPage.styles';
import useProcessingActivitiesListData from './useProcessingActivitiesListData';
import useProcessingActivitiesMetrics from './useProcessingActivitiesMetrics';

const PAGE_SIZE = 50;

const ProcessingActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const { showHelpDialog } = useHelpDialogContext();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canManageProcessingActivities = canAccessProcessingActivitiesAdmin(userData);

  usePageTitle(t(processing_activities_messages.page_title));

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProcessingActivitiesFilterValues>(DEFAULT_PROCESSING_ACTIVITIES_FILTERS);
  const [sort, setSort] = useState<ProcessingActivitiesSort>({ key: null, direction: null });
  const queryClient = useQueryClient();

  const activeReviewQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_ACTIVE, organizationId],
    async () => {
      const { data } = await ropaReviewsApi.getActiveReview(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && canManageProcessingActivities),
      retry: false,
    },
  );

  const archiveMutation = useMutation(
    MUTATION_KEYS.PROCESSING_ACTIVITIES_ARCHIVE,
    async (processingActivityId: number) => {
      await processingActivitiesApi.archive(organizationId as number, processingActivityId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId]);
        await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_METRICS, organizationId]);
        enqueueSnackbar(t(processing_activities_messages.archive_confirm.success), { variant: 'success' });
      },
      onError: (error: unknown) => {
        enqueueSnackbar(t(getProcessingActivityArchiveErrorMessage(error)), { variant: 'error' });
      },
    },
  );

  const { accumulatedActivities, hasNextPage, isError, isFetching, isForbidden, isLoading, loadNextPage, refetch } =
    useProcessingActivitiesListData({
      filters,
      organizationId,
      pageSize: PAGE_SIZE,
      search: searchTerm,
      sort,
    });
  const { isLoading: metricsLoading, metrics } = useProcessingActivitiesMetrics(organizationId);

  const availableTableFilters = useMemo(() => buildProcessingActivitiesTableAvailableFilters(), []);
  const tableFilters = useMemo(() => processingActivitiesFilterValuesToTableFilters(filters), [filters]);

  const setTableFilters = useCallback((updater: React.SetStateAction<ProcessingActivitiesTableFilterState>) => {
    setFilters(previousFilters => {
      const currentTableFilters = processingActivitiesFilterValuesToTableFilters(previousFilters);
      const nextTableFilters = typeof updater === 'function' ? updater(currentTableFilters) : updater;
      return mergeTableFiltersIntoFilterValues(previousFilters, nextTableFilters);
    });
  }, []);

  const filtersService = useMemo(
    () => ({
      availableFilters: availableTableFilters,
      filters: tableFilters,
      setFilters: setTableFilters,
    }),
    [availableTableFilters, setTableFilters, tableFilters],
  );

  const openCreatePage = () => {
    history.push(PATHS.PROCESSING_ACTIVITIES_CREATE);
  };

  const openEditDraftPage = (activity: ProcessingActivity) => {
    history.push(PATHS.PROCESSING_ACTIVITIES_EDIT.replace(':id', String(activity.id)));
  };

  const openActivityDetail = (activity: ProcessingActivity) => {
    history.push(PATHS.PROCESSING_ACTIVITIES_DETAIL.replace(':id', String(activity.id)));
  };

  const handleArchive = useCallback(
    async (activity: ProcessingActivity) => {
      const confirmed = await showConfirmationModal({
        title: t(processing_activities_messages.archive_confirm.title),
        body: t(processing_activities_messages.archive_confirm.body, { name: activity.name }),
      });

      if (!confirmed) return;

      archiveMutation.mutate(activity.id);
    },
    [archiveMutation, showConfirmationModal, t],
  );

  const handleSort = (key: string, direction: string) => {
    setSort({ key, direction });
  };

  const openHelpDialog = () => {
    showHelpDialog({
      title: t(processing_activities_messages.help.title),
      body: t(processing_activities_messages.help.body),
    });
  };

  const openAssetRopaPage = () => {
    history.push(PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS, { fromProcessingActivities: true });
  };

  const showInitialLoader = !isForbidden && isLoading && accumulatedActivities.length === 0;
  const isArchivedView = filters.status === 'archived';
  const hasActiveFilters = hasProcessingActivitiesListFiltersApplied(filters, searchTerm);

  const tableEmptyState = useMemo(() => {
    if (hasActiveFilters) {
      return (
        <>
          <Typography variant='h6'>{t(processing_activities_messages.empty_state.filtered_title)}</Typography>
          <Typography color='textSecondary'>{t(processing_activities_messages.empty_state.filtered_description)}</Typography>
        </>
      );
    }

    return (
      <>
        <Typography variant='h6'>
          {t(isArchivedView ? processing_activities_messages.empty_state.archived_title : processing_activities_messages.empty_state.title)}
        </Typography>
        <Typography color='textSecondary'>
          {t(
            isArchivedView
              ? processing_activities_messages.empty_state.archived_description
              : processing_activities_messages.empty_state.description,
          )}
        </Typography>
      </>
    );
  }, [hasActiveFilters, isArchivedView, t]);

  const searchStatusMessage = useMemo(() => {
    if (isForbidden) {
      return '';
    }

    if (showInitialLoader || (isLoading && accumulatedActivities.length === 0)) {
      return t(processing_activities_messages.search_status.loading);
    }

    if (accumulatedActivities.length === 0) {
      if (hasActiveFilters) {
        return `${t(processing_activities_messages.empty_state.filtered_title)}. ${t(
          processing_activities_messages.empty_state.filtered_description,
        )}`;
      }

      return `${t(
        isArchivedView ? processing_activities_messages.empty_state.archived_title : processing_activities_messages.empty_state.title,
      )}. ${t(
        isArchivedView
          ? processing_activities_messages.empty_state.archived_description
          : processing_activities_messages.empty_state.description,
      )}`;
    }

    return t(processing_activities_messages.search_status.results_count, { count: accumulatedActivities.length });
  }, [accumulatedActivities.length, hasActiveFilters, isArchivedView, isForbidden, isLoading, showInitialLoader, t]);

  let content: React.ReactNode;

  if (isForbidden) {
    content = (
      <Box className={styles.forbiddenState}>
        <Typography gutterBottom variant='h6'>
          {t(processing_activities_messages.forbidden_title)}
        </Typography>
        <Typography color='textSecondary' variant='body2'>
          {t(processing_activities_messages.forbidden_body)}
        </Typography>
        <Button className={styles.forbiddenBackButton} color='primary' onClick={() => history.push(PATHS.ROOT)} variant='contained'>
          {t(processing_activities_messages.forbidden_back)}
        </Button>
      </Box>
    );
  } else if (showInitialLoader) {
    content = <Loader inner />;
  } else if (isError && accumulatedActivities.length === 0) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' gutterBottom variant='body2'>
          {t(processing_activities_messages.error_state)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    content = (
      <>
        <ProcessingActivitiesMetricsSection isLoading={metricsLoading} metrics={metrics} />
        <ProcessingActivitiesToolbar filters={filters} onFiltersChange={setFilters} onSearch={setSearchTerm} searchValue={searchTerm} />
        <ProcessingActivitiesTable
          data={accumulatedActivities}
          emptyStateContent={tableEmptyState}
          filtersService={filtersService}
          loading={isFetching}
          onArchive={handleArchive}
          onEditDraft={openEditDraftPage}
          onOpenActivity={openActivityDetail}
          onSort={handleSort}
          sort={sort}
        />
        {hasNextPage && (
          <FooterButton className='' loading={isFetching} onClick={loadNextPage}>
            {t(general_messages.show_more)}
          </FooterButton>
        )}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <Box className={styles.headerActions}>
              <TextButton aria-label={t(general_messages.how_it_works)} onClick={openHelpDialog}>
                {t(general_messages.how_it_works)}
              </TextButton>
              {canManageProcessingActivities && !activeReviewQuery.isLoading && (
                <Button onClick={openAssetRopaPage} variant='outlined'>
                  {t(processing_activities_messages.asset_ropa.actions.go_to_ropa_review)}
                </Button>
              )}
              <ColoredButton
                className={styles.createButton}
                customColor='secondary'
                disabled={!canManageProcessingActivities}
                icon={false}
                onClick={openCreatePage}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                <PlusIcon fontSize='small' />
                {t(processing_activities_messages.actions.create)}
              </ColoredButton>
            </Box>
          }
          subtitle={t(processing_activities_messages.page_subtitle)}
        >
          {t(processing_activities_messages.page_title)}
        </ManagePageHeader>
        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(processing_activities_messages.page_help)}
        </Typography>
        <LiveRegionStatus message={searchStatusMessage} />
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default ProcessingActivitiesPage;
